import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import Button from './Button';
import Modal from './Modal';

const ConfirmContext = createContext(null);

/**
 * Drop-in async replacement for window.confirm.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm('Delete this user?', { tone: 'danger', confirmLabel: 'Delete' });
 *   if (!ok) return;
 *
 * Wrap the app once with <ConfirmProvider>.
 *
 * Infrastructure (backdrop, focus trap, Escape handling, click-outside-
 * close, ARIA dialog semantics, scale-in animation) comes from the
 * design-system <Modal> primitive. ConfirmDialog owns only the
 * Promise-based async orchestration and the Enter-to-confirm shortcut.
 *
 * `tone: 'danger'` confirmations render role="alertdialog" instead of
 * the default role="dialog", per WAI-ARIA guidance reserving alertdialog
 * for interruptions that require an immediate, consequential response
 * (e.g. a destructive-action confirmation) rather than routine dialogs.
 */
export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // { message, options, resolve }
  const confirmBtnRef = useRef(null);

  const confirm = useCallback((message, options = {}) => (
    new Promise((resolve) => {
      setState({ message, options, resolve });
    })
  ), []);

  const handleClose = useCallback((value) => {
    setState((current) => {
      current?.resolve?.(value);
      return null;
    });
  }, []);

  useEffect(() => {
    if (!state) return undefined;

    // Enter-to-confirm shortcut is ConfirmDialog-specific behavior;
    // Escape, backdrop click, and the close button are owned by Modal.
    const handleEnter = (event) => {
      if (event.key === 'Enter') {
        handleClose(true);
      }
    };
    window.addEventListener('keydown', handleEnter);
    return () => {
      window.removeEventListener('keydown', handleEnter);
    };
  }, [state, handleClose]);

  const isDanger = state?.options?.tone === 'danger';
  const title = state?.options?.title || 'Confirm action';
  const confirmLabel = state?.options?.confirmLabel || 'Confirm';
  const cancelLabel = state?.options?.cancelLabel || 'Cancel';

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal
        open={Boolean(state)}
        onClose={() => handleClose(false)}
        title={title}
        role={isDanger ? 'alertdialog' : 'dialog'}
        size="sm"
        initialFocusRef={confirmBtnRef}
        footer={(
          <>
            <Button size="sm" variant="secondary" onClick={() => handleClose(false)}>
              {cancelLabel}
            </Button>
            <Button
              ref={confirmBtnRef}
              variant={isDanger ? 'danger' : 'primary'}
              onClick={() => handleClose(true)}
            >
              {confirmLabel}
            </Button>
          </>
        )}
      >
        <p>{state?.message}</p>
      </Modal>
    </ConfirmContext.Provider>
  );
}

/**
 * Returns an async confirm(message, options?) function.
 * Resolves to true if the user confirms, false otherwise.
 *
 * options:
 *   - title:        Heading above the message (default 'Confirm action').
 *   - confirmLabel: Primary button text (default 'Confirm').
 *   - cancelLabel:  Secondary button text (default 'Cancel').
 *   - tone:         'danger' renders the primary button red.
 */
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used inside a <ConfirmProvider>.');
  }
  return ctx;
}
